import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faCircleInfo,
  faLeaf,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { cn } from 'modules/Common/utils/cn';
import { useAllCountries } from 'modules/Common/utils/Utility';
import { countryDisplayName, findCountry } from 'helpers/countries';
import { useLocaleStore } from 'stores/useLocaleStore';
import {
  changesCountry,
  groupByPeriod,
  lineDelta,
  lineKey,
  selectionState,
  toggleAll,
  toggleLines,
  unitLabel,
} from 'modules/Common/utils/assignImports';

dayjs.extend(utc);

const fmtDate = (value) => (value ? dayjs.utc(value).format('DD/MM/YYYY') : '—');
const fmtNumber = (value, digits) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : '—';

// The app's checkbox (modules/Common/form/CgCheckbox) with a partial state: a
// native input keeps the keyboard and screen-reader contract, the dash is drawn
// over it when `indeterminate`.
const CHECKBOX_CLASS =
  'h-4 w-4 rounded checkbox-white-tick appearance-none border border-black/25 bg-white ' +
  'bg-contain bg-center bg-no-repeat align-middle cursor-pointer ' +
  'checked:bg-primaryColor checked:border-primaryColor';

function TriStateCheckbox({ state, onChange, label, id }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'partial';
  }, [state]);
  return (
    <span className="relative inline-flex shrink-0">
      <input
        ref={ref}
        id={id}
        type="checkbox"
        aria-label={label}
        checked={state === 'all'}
        onChange={onChange}
        className={cn(CHECKBOX_CLASS, state === 'partial' && 'bg-primaryColor border-primaryColor')}
      />
      {state === 'partial' && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-[3px] top-[7px] h-[2px] w-[10px] rounded bg-white"
        />
      )}
    </span>
  );
}

const ROW_GRID =
  'grid grid-cols-[minmax(0,1.6fr)_88px_72px_minmax(0,1fr)_minmax(0,1fr)_64px] gap-3';

/**
 * The "Assign installation to imports" step of the add-emissions dialog
 * (backend docs/features/import-installation-backfill, D10): the factor about
 * to be saved, the supplier-only imports it covers, and which of them get the
 * installation. Selection is owned by the caller.
 */
export default function AssignImportsStep({
  preview,
  factor,
  selected,
  onSelectionChange,
  isUk,
  idPrefix = 'assign-imports',
}) {
  const { t } = useTranslation('installations');
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const lines = preview.lines;
  const groups = groupByPeriod(lines);
  const parentState = selectionState(lines, selected);
  const installationName = preview.installation?.name;
  // Assigning sets a line's country of origin to the installation's: flag the lines
  // where that changes it, but keep them selected — the user decides.
  const countries = useAllCountries();
  const installationCountry = preview.installation?.country;
  const locale = useLocaleStore((state) => state.locale);
  const countryName = (id) => countryDisplayName(findCountry(countries, id), locale) || String(id);
  const movesCountry = (line) => changesCountry(line, installationCountry);
  const countryChanges = lines.filter(movesCountry).length;

  const toggleGroupOpen = (key) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleLine = (line) => {
    const next = new Set(selected);
    if (next.has(lineKey(line))) next.delete(lineKey(line));
    else next.add(lineKey(line));
    onSelectionChange(next);
  };

  const periodTitle = (group) =>
    group.quarter
      ? t('assignImports.quarterTitle', '{{quarter}} {{year}}', {
          quarter: group.quarter,
          year: group.year,
        })
      : group.year;

  return (
    <div className="flex flex-col gap-3">
      {/* The factor about to be saved, on one line. */}
      <div className="flex flex-wrap md:flex-nowrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs">
        <FontAwesomeIcon
          icon={faLeaf}
          className="text-primaryColor text-[11px] shrink-0"
        />
        <span className="font-mono font-semibold text-slate-800 whitespace-nowrap">
          {factor.cnGood}
        </span>
        <span
          className="truncate text-slate-500 min-w-0 flex-1"
          title={factor.cnName}
        >
          {factor.cnName}
        </span>
        <span className="text-slate-600 whitespace-nowrap">
          {fmtDate(factor.date)} – {fmtDate(factor.lastRecordDate)}
        </span>
        <span
          className="tabular-nums whitespace-nowrap text-slate-600"
          title={t(
            'assignImports.valuesTitle',
            'Direct {{direct}} · Indirect {{indirect}} · Total {{total}} tCO₂/t',
            {
              direct: fmtNumber(factor.direct, 4),
              indirect: fmtNumber(factor.indirect, 4),
              total: fmtNumber(factor.total, 4),
            },
          )}
        >
          {fmtNumber(factor.direct, 4)} + {fmtNumber(factor.indirect, 4)} ={' '}
          <strong className="text-slate-800">{fmtNumber(factor.total, 4)}</strong> tCO₂/t
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-md border bg-green-50 text-green-700 border-green-100 whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
          {t('assignImports.actual', 'Actual')}
        </span>
      </div>

      <p className="m-0 flex items-start gap-2 text-sm text-slate-600">
        <FontAwesomeIcon
          icon={faCircleInfo}
          className="text-slate-400 text-[13px] shrink-0 mt-[3px]"
        />
        <span>
          <strong className="text-slate-800">
            {t('assignImports.importsCount', {
              count: lines.length,
              defaultValue: '{{count}} import',
              defaultValue_other: '{{count}} imports',
            })}
          </strong>{' '}
          {isUk
            ? t(
                'assignImports.infoUk',
                'from this supplier have commodity code {{code}}, no installation, and a clearance date this factor applies to (on or after its start, before any newer factor).',
                { code: factor.cnGood },
              )
            : t(
                'assignImports.info',
                'from this supplier have CN good {{code}}, no installation, and a clearance date this factor applies to (on or after its start, before any newer factor).',
                { code: factor.cnGood },
              )}
        </span>
      </p>

      {countryChanges > 0 && (
        <p className="m-0 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="text-amber-500 text-[12px] shrink-0 mt-[2px]"
          />
          <span>
            {t('assignImports.countryNotice', {
              count: countryChanges,
              country: countryName(installationCountry),
              defaultValue:
                "{{count}} import has a different country of origin. Assigning changes it to {{country}}, the installation's country.",
              defaultValue_other:
                "{{count}} imports have a different country of origin. Assigning changes it to {{country}}, the installation's country.",
            })}
          </span>
        </p>
      )}

      <div className="rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100 bg-white max-h-[50vh] overflow-y-auto">
        <label
          htmlFor={`${idPrefix}-all`}
          className="flex items-center gap-3 px-4 py-2.5 bg-slate-50/60 cursor-pointer"
        >
          <TriStateCheckbox
            id={`${idPrefix}-all`}
            state={parentState}
            label={t('assignImports.assignTo', 'Assign {{installation}} to these imports', {
              installation: installationName,
            })}
            onChange={() => onSelectionChange(toggleAll(lines, selected))}
          />
          <span className="text-sm font-medium text-slate-800">
            {t('assignImports.assignTo', 'Assign {{installation}} to these imports', {
              installation: installationName,
            })}
          </span>
          <span className="ml-auto text-xs text-slate-500 tabular-nums whitespace-nowrap">
            {t('assignImports.selectedCount', '{{selected}} of {{total}} selected', {
              selected: selected.size,
              total: lines.length,
            })}
          </span>
        </label>

        {groups.map((group) => {
          const open = openGroups.has(group.key);
          const groupSelected = group.lines.filter((l) => selected.has(lineKey(l))).length;
          return (
            <div key={group.key}>
              <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                {/* px-4 + w-6 + gap-1 = 2.75rem = the line rows' pl-11, so the checkboxes line up */}
                <span className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => toggleGroupOpen(group.key)}
                    aria-expanded={open}
                    aria-label={periodTitle(group)}
                    className="grid h-6 w-6 place-items-center rounded-md text-slate-400 hover:bg-slate-100"
                  >
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className={cn('text-[11px] transition-transform', open && 'rotate-90')}
                    />
                  </button>
                  <TriStateCheckbox
                    id={`${idPrefix}-group-${group.key}`}
                    state={selectionState(group.lines, selected)}
                    label={periodTitle(group)}
                    onChange={() => onSelectionChange(toggleLines(group.lines, selected))}
                  />
                </span>
                {/* A mouse target for the whole title; the arrow is the keyboard control. */}
                <div
                  onClick={() => toggleGroupOpen(group.key)}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
                >
                  <span className="text-sm font-medium text-slate-700">{periodTitle(group)}</span>
                  <span className="text-xs text-slate-400">
                    {group.quarter
                      ? t('assignImports.quarterlyReport', 'Quarterly report')
                      : t('assignImports.annualReport', 'Annual report')}
                  </span>
                  {group.reportGenerated && (
                    <span
                      className="text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap"
                      title={t(
                        'assignImports.reportGeneratedHelp',
                        'A CBAM report was already generated for this period. Assigning changes the values behind it.',
                      )}
                    >
                      {t('assignImports.reportGenerated', 'Report generated')}
                    </span>
                  )}
                  {group.lines.some(movesCountry) && (
                    <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                      {t('assignImports.countryChanges', 'Country changes')}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-slate-500 tabular-nums whitespace-nowrap">
                    {t('assignImports.groupCount', '{{selected}} of {{total}}', {
                      selected: groupSelected,
                      total: group.lines.length,
                    })}
                  </span>
                </div>
              </div>

              {open && (
                <div className="overflow-x-auto">
                  <div className="min-w-[600px] divide-y divide-slate-50">
                    <div
                      className={cn(
                        ROW_GRID,
                        'py-1.5 pl-[4.25rem] pr-4 text-[10px] font-semibold uppercase tracking-wide text-slate-400',
                      )}
                    >
                      <span>{t('assignImports.columns.import', 'Import')}</span>
                      <span>{t('assignImports.columns.cleared', 'Cleared')}</span>
                      <span className="text-right">
                        {t('assignImports.columns.quantity', 'Qty')}
                      </span>
                      <span>{t('assignImports.columns.current', 'Current')}</span>
                      <span>{t('assignImports.columns.new', 'New')}</span>
                      <span className="text-right">
                        {t('assignImports.columns.delta', 'Δ tCO₂e')}
                      </span>
                    </div>
                    {group.lines.map((line) => {
                      const key = lineKey(line);
                      const isSelected = selected.has(key);
                      const delta = isSelected ? lineDelta(line, factor.total) : null;
                      return (
                        <label
                          key={key}
                          className="flex items-center gap-3 py-1.5 pl-11 pr-4 text-xs cursor-pointer hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            className={CHECKBOX_CLASS}
                            checked={isSelected}
                            onChange={() => toggleLine(line)}
                            aria-label={line.internalId || key}
                          />
                          <span className={cn(ROW_GRID, 'min-w-0 flex-1 items-center')}>
                            {/* The ID on its own line, so tags never squeeze it; tags wrap below. */}
                            <span className="flex min-w-0 flex-col gap-0.5">
                              <span
                                className="truncate font-mono text-slate-600"
                                title={line.internalId || undefined}
                              >
                                {line.internalId || '—'}
                              </span>
                              {(line.kind === 'purchase' || movesCountry(line)) && (
                                <span className="flex min-w-0 flex-wrap items-center gap-1">
                                  {line.kind === 'purchase' && (
                                    <span
                                      className="max-w-full truncate text-[10px] font-medium px-1 py-px rounded bg-slate-50 text-slate-600 border border-slate-200"
                                      title={t(
                                        'assignImports.purchaseHelp',
                                        'A purchase (goods bought, no customs data). It is not in the CBAM report.',
                                      )}
                                    >
                                      {t('assignImports.purchase', 'Purchase')}
                                    </span>
                                  )}
                                  {movesCountry(line) && (
                                    <span
                                      className="max-w-full truncate text-[10px] font-medium px-1 py-px rounded bg-amber-50 text-amber-700 border border-amber-200"
                                      title={t(
                                        'assignImports.countryChangeHelp',
                                        "Assigning changes this import's country of origin to the installation's country.",
                                      )}
                                    >
                                      {t('assignImports.countryChange', '{{from}} → {{to}}', {
                                        from: countryName(line.countryOfOrigin),
                                        to: countryName(installationCountry),
                                      })}
                                    </span>
                                  )}
                                </span>
                              )}
                            </span>
                            <span className="text-slate-600">{fmtDate(line.deliveryDate)}</span>
                            <span className="text-right font-semibold text-slate-800 tabular-nums">
                              {fmtNumber(line.quantity, 1)} {unitLabel(line.unit)}
                            </span>
                            <span
                              className="truncate text-slate-600 tabular-nums"
                              title={t(
                                'assignImports.valuesTitle',
                                'Direct {{direct}} · Indirect {{indirect}} · Total {{total}} tCO₂/t',
                                {
                                  direct: fmtNumber(line.current?.direct, 4),
                                  indirect: fmtNumber(line.current?.indirect, 4),
                                  total: fmtNumber(line.current?.total, 4),
                                },
                              )}
                            >
                              <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" />
                              {fmtNumber(line.current?.total, 2)}
                            </span>
                            <span className="truncate text-slate-600 tabular-nums">
                              <span
                                className={cn(
                                  'mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle',
                                  isSelected ? 'bg-emerald-500' : 'bg-slate-300',
                                )}
                              />
                              {isSelected ? fmtNumber(factor.total, 2) : '—'}
                            </span>
                            <span
                              className={cn(
                                'text-right tabular-nums',
                                delta == null
                                  ? 'text-slate-400'
                                  : delta <= 0
                                    ? 'text-emerald-700'
                                    : 'text-amber-700',
                              )}
                            >
                              {delta == null
                                ? '—'
                                : `${delta > 0 ? '+' : '−'}${Math.abs(delta).toFixed(1)}`}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selected.size === 0 && (
        <p className="m-0 text-xs text-slate-500">
          {t('assignImports.factorOnly', 'Only the emission factor will be saved.')}
        </p>
      )}
    </div>
  );
}
